import { HttpClient } from '@angular/common/http';
import { Observable, ReplaySubject, AsyncSubject } from 'rxjs';
import { Injectable, inject } from '@angular/core';
import { Constants } from './constants';
import { IdentityRepository, IdentityUser, Menu } from '@model/identity';
import { IdentityOperations } from '@model/identity/operations';
import { dateWithTimeToString, fromMills } from '@common/date-utils';
import { APP_CONFIG, AppConfig } from '@cfg/env/app.config';
import { share } from 'rxjs/operators';
import { WINDOW } from '@helpers/routing/router.service';

/**
 * Implementación del servicio de identidad
 */
@Injectable({
  providedIn: 'root'
})
export class IdentityServiceImpl extends IdentityRepository {
  private readonly appConfig: AppConfig = inject(APP_CONFIG);

  /** representa el usuario logueado en el sistema */
  private user?: IdentityUser = undefined;

  /** referencia al objeto global window para redirecciones testeables */
  private readonly windowRef = inject(WINDOW);

  /** Subject para notificar la informacion del usuario  */
  private authState = new ReplaySubject<IdentityUser>(1);

  /**
   *  Almacena una referencia hacia el observable que obtiene la información del usuario y lo marca de tipo share para que
   *  no se haga una misma peticion multiples veces
   */
  private observerLoginInfo?: Observable<IdentityUser> = undefined;

  /**
   * Almacena una referencia al observable de URL de logout para evitar llamadas HTTP repetidas.
   */
  private observerLogoutUrl?: Observable<string> = undefined;

  /**
   * servicio para hacer peticiones http
   */
  private readonly http = inject(HttpClient);

  /**
   * Esta función se encarga de enviar por el authState el usuario, ya sea que lo recupere del sessionStorage o del servicio
   * expuesto en el backend
   */
  refreshUserInfo(): void {
    this.observerLoginInfo = this.http
      .get<IdentityUser>(
        this.appConfig.backend.context + Constants.securityUserInfo
      )
      .pipe(
        share({
          connector: () => new AsyncSubject(),
          resetOnError: false,
          resetOnComplete: false,
          resetOnRefCountZero: false
        })
      );

    this.observerLoginInfo.subscribe({
      next: (user) => {
        if (!user) {
          this.user = undefined;
          this.authState.next({} as IdentityUser);
          return;
        }

        this.user = user;
        this.user.lastAccess = dateWithTimeToString(
          fromMills(Number(user.lastAccess))
        );
        this.authState.next(user);
      },
      error: (error) => {
        this.authState.error(error);
      }
    });
  }

  /**
   * Devuelve un Observable en el cual se notificará el usuario en la sesión.
   *
   * @returns devuelve un stream donde se puede obtener la información del usuario logueado
   */
  getUserInfo(): Observable<IdentityUser> {
    if (!this.observerLoginInfo) {
      this.refreshUserInfo();
    }

    return this.authState.asObservable();
  }

  /**
   * Devuelve el menu asociado a el usuario
   *
   * @returns devuelve el menu del usuario
   */
  getUserMenu(): Promise<Menu[]> {
    return new Promise((resolver) => {
      if (this.user != null) {
        resolver(IdentityOperations.getMenu(this.user));
      } else {
        this.getUserInfo().subscribe((user) => {
          resolver(IdentityOperations.getMenu(user));
        });
      }
    });
  }

  /**
   * Comprueba si el usuario tiene una sesión activa en el backend
   *
   * @returns Observable<boolean> true si la sesión está activa
   */
  checkSession(): Observable<boolean> {
    return this.http.get<boolean>(
      this.appConfig.backend.context + Constants.securityCheckStatus
    );
  }

  /**
   * Esta funcion comprueba si un usuario esta logueado en el sistema
   *
   * @returns indica si el usuario esta logueado en el sistema o no
   */
  isLoggedIn(): boolean {
    return this.user != null;
  }

  /**
   * Con esta funcion se puede comprobar si el usuario en la sesión tiene permiso hacia un recurso especifico
   *
   * @param resource url del recurso
   * @returns indica si un usuario tiene acceso a un recurso específico
   */
  isAuthorized(resource: string): Observable<boolean> {
    const body = {
      method: 'GET',
      path: resource
    };
    return this.http.post<boolean>(
      this.appConfig.backend.context + Constants.securityUserAccess,
      body
    );
  }

  /**
   * Obtiene la URL de logout desde el backend
   *
   * @returns Observable con la URL de logout
   */
  getLogoutUrl(): Observable<string> {
    if (!this.observerLogoutUrl) {
      const options = { responseType: 'text' as const };
      this.observerLogoutUrl = this.http
        .get(
          this.appConfig.backend.context + Constants.securityLogoutUrl,
          options
        )
        .pipe(
          share({
            connector: () => new AsyncSubject(),
            resetOnError: false,
            resetOnComplete: false,
            resetOnRefCountZero: false
          })
        );
    }

    return this.observerLogoutUrl;
  }

  /**
   * Esta funcion invoca el logout completo del sistema, aqui si se cierra la sesión y el usuario debería ingresar sus
   * credenciales si desea volver a ingresar.
   */
  fullLogout(fullUrlLogout: string): void {
    // Limpia la sesión local
    this.clearObserverForLogin();
    // Redirige a la URL de logout
    this.windowRef.location.href = fullUrlLogout || '';
  }

  /**
   * Esta función se encarga de eliminar la referencia del Observable que devuelve la información del usuario logueado
   * de esta forma se puede hacer refresh de dicha información solicitandola al backend
   */
  clearObserverForLogin(): void {
    this.observerLoginInfo = undefined;
    this.observerLogoutUrl = undefined;
    this.user = undefined;
    this.authState = new ReplaySubject<IdentityUser>(1);
  }
}
